// src/components/operationCenter/widgets/PortfolioHoldingsWidget.tsx
// Portfolio holdings table widget

import React, { useMemo } from "react";
import { TrendingUp, TrendingDown, Briefcase } from "lucide-react";
import { Link } from "react-router-dom";
import type { WidgetProps } from "@/types/dashboard";
import { usePortfolioHoldings } from "@/hooks/portfolio";
import { useAccessToken } from "@/hooks/useAccessToken";
import { useTokensScreenerData } from "@/hooks/tokens/useTokensScreenerData";
import { useTokensChanges } from "@/hooks/tokens/compare/useCompareData";
import { MetricKeys } from "@/constants/metrics";

const PortfolioHoldingsWidget: React.FC<WidgetProps> = () => {
  const authToken = useAccessToken();
  const { data: holdingsData, isLoading: holdingsLoading } =
    usePortfolioHoldings(authToken);
  const { data: screenerRows } = useTokensScreenerData();
  const { changeByTicker } = useTokensChanges();

  const holdings = useMemo(() => {
    const rawHoldings = holdingsData?.data ?? [];
    if (rawHoldings.length === 0 || !Array.isArray(screenerRows)) return [];

    return rawHoldings
      .map((h) => {
        const ticker = h.ticker.toUpperCase();
        const row = screenerRows.find(
          (r) => String(r["tokenSymbol"] ?? "").toUpperCase() === ticker
        );
        const changes = changeByTicker.get(ticker);

        const currentPrice = row?.[MetricKeys.PriceClose] as number | null;
        const change24h = changes?.[`${MetricKeys.PriceClose}_1d`] as
          | number
          | null;
        const currentValue = currentPrice ? h.quantity * currentPrice : null;
        const costBasis = h.quantity * h.avg_buy_price;
        const pnl = currentValue ? currentValue - costBasis : null;
        const pnlPct =
          costBasis > 0 && pnl !== null ? (pnl / costBasis) * 100 : null;

        return {
          ticker,
          name: h.token_name ?? ticker,
          logoUrl: h.logo_url,
          quantity: h.quantity,
          avgBuyPrice: h.avg_buy_price,
          currentPrice,
          currentValue,
          change24h,
          pnl,
          pnlPct,
        };
      })
      .sort((a, b) => (b.currentValue ?? 0) - (a.currentValue ?? 0));
  }, [holdingsData, screenerRows, changeByTicker]);

  const formatValue = (val: number | null) => {
    if (val === null) return "—";
    return `$${val.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatPrice = (price: number | null | undefined) => {
    if (price == null) return "—";
    if (price >= 1000)
      return `$${price.toLocaleString(undefined, {
        maximumFractionDigits: 0,
      })}`;
    if (price >= 1) return `$${price.toFixed(2)}`;
    return `$${price.toPrecision(4)}`;
  };

  if (holdingsLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="animate-pulse text-slate-500 text-sm">Loading...</div>
      </div>
    );
  }

  if (holdings.length === 0) {
    return (
      <div className="h-full flex flex-col items-center justify-center text-slate-500 p-4">
        <Briefcase className="w-8 h-8 mb-2 opacity-50" />
        <div className="text-sm">No holdings found</div>
        <div className="text-xs mt-1">
          Add transactions to see your portfolio
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-slate-700/30 flex-shrink-0">
        <div className="flex items-center gap-2">
          <Briefcase className="w-3.5 h-3.5 text-slate-400" />
          <span className="text-xs uppercase tracking-wide text-slate-300 font-medium">
            My Holdings
          </span>
        </div>
        <span className="text-[10px] text-slate-500">
          {holdings.length} assets
        </span>
      </div>

      {/* Scrollable list */}
      <div className="flex-1 overflow-y-auto">
        <table className="w-full text-xs">
          <thead className="sticky top-0 bg-slate-900/95 border-b border-slate-700/50">
            <tr>
              <th className="text-left text-slate-500 font-medium px-3 py-2">
                Asset
              </th>
              <th className="text-right text-slate-500 font-medium px-2 py-2">
                Value
              </th>
              <th className="text-right text-slate-500 font-medium px-3 py-2">
                P&L
              </th>
            </tr>
          </thead>
          <tbody>
            {holdings.map((h) => {
              const isUp = (h.pnlPct ?? 0) >= 0;
              return (
                <tr
                  key={h.ticker}
                  className="border-b border-slate-800/50 hover:bg-slate-800/30 transition-colors"
                >
                  <td className="px-3 py-2">
                    <Link
                      to={`/app/tokens/${h.ticker.toLowerCase()}`}
                      className="flex items-center gap-2 hover:text-blue-400"
                    >
                      {h.logoUrl && (
                        <img
                          src={h.logoUrl}
                          alt=""
                          className="w-5 h-5 rounded-full"
                        />
                      )}
                      <div>
                        <div className="font-semibold text-slate-200">
                          {h.ticker}
                        </div>
                        <div className="text-[10px] text-slate-500">
                          {h.quantity.toFixed(4)} @ {formatPrice(h.avgBuyPrice)}
                        </div>
                      </div>
                    </Link>
                  </td>
                  <td className="text-right px-2 py-2">
                    <div className="font-medium text-slate-200">
                      {formatValue(h.currentValue)}
                    </div>
                    <div className="text-[10px] text-slate-500">
                      {formatPrice(h.currentPrice)}
                    </div>
                  </td>
                  <td className="text-right px-3 py-2">
                    <div
                      className={`flex items-center justify-end gap-1 font-medium ${
                        isUp ? "text-emerald-400" : "text-rose-400"
                      }`}
                    >
                      {isUp ? (
                        <TrendingUp className="w-3 h-3" />
                      ) : (
                        <TrendingDown className="w-3 h-3" />
                      )}
                      {h.pnlPct !== null
                        ? `${h.pnlPct >= 0 ? "+" : ""}${h.pnlPct.toFixed(1)}%`
                        : "—"}
                    </div>
                    <div
                      className={`text-[10px] ${
                        isUp ? "text-emerald-500/70" : "text-rose-500/70"
                      }`}
                    >
                      {h.pnl !== null ? formatValue(h.pnl) : "—"}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PortfolioHoldingsWidget;
